import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Plus, 
  Package, 
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Search,
  RefreshCw,
  Trash2,
  Edit,
  ShoppingCart,
  AlertCircle,
  FileText,
  History
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

// Use relative path for API calls (works on any domain)
const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
  ? '/api' 
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'meats', label: 'Carnes' },
  { value: 'vegetables', label: 'Verduras' },
  { value: 'fruits', label: 'Frutas' },
  { value: 'dairy', label: 'Lácteos' },
  { value: 'drinks', label: 'Bebidas' },
  { value: 'alcohol', label: 'Alcohol' },
  { value: 'dry_goods', label: 'Secos' },
  { value: 'frozen', label: 'Congelados' },
  { value: 'cleaning', label: 'Limpieza' },
  { value: 'general', label: 'General' }
];

const UNITS = [
  { value: 'unidad', label: 'Unidad' },
  { value: 'kg', label: 'Kilogramo (kg)' },
  { value: 'g', label: 'Gramo (g)' },
  { value: 'litro', label: 'Litro (L)' },
  { value: 'ml', label: 'Mililitro (ml)' },
  { value: 'caja', label: 'Caja' },
  { value: 'pack', label: 'Pack' }
];

export default function InventoryPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ingredients, setIngredients] = useState([]);
  const [movements, setMovements] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedIngredient, setSelectedIngredient] = useState(null);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showMovementDialog, setShowMovementDialog] = useState(false);
  
  // Form states
  const [form, setForm] = useState({
    name: '',
    category: 'general',
    unit: 'unidad',
    current_stock: 0,
    min_stock: 0,
    cost_per_unit: 0,
    supplier: ''
  });
  const [movementForm, setMovementForm] = useState({
    movement_type: 'purchase',
    quantity: '',
    unit_cost: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ingredientsRes, alertsRes, movementsRes] = await Promise.all([
        axios.get(`${API}/ingredients`, { withCredentials: true }),
        axios.get(`${API}/stock/alerts`, { withCredentials: true }),
        axios.get(`${API}/stock-movements`, { withCredentials: true })
      ]);
      setIngredients(ingredientsRes.data);
      setAlerts(alertsRes.data.alerts || []);
      setMovements(movementsRes.data);
    } catch (error) {
      toast.error('Error al cargar inventario');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIngredient = async () => {
    try {
      await axios.post(`${API}/ingredients`, form, { withCredentials: true });
      toast.success('Insumo creado');
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Error al crear insumo');
    }
  };

  const handleUpdateIngredient = async () => {
    if (!selectedIngredient) return;
    try {
      await axios.put(`${API}/ingredients/${selectedIngredient.ingredient_id}`, form, { withCredentials: true });
      toast.success('Insumo actualizado');
      setShowEditDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar insumo');
    }
  };

  const handleDeleteIngredient = async (id) => {
    if (!confirm('¿Eliminar este insumo?')) return;
    try {
      await axios.delete(`${API}/ingredients/${id}`, { withCredentials: true });
      toast.success('Insumo eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar insumo');
    }
  };

  const handleAddMovement = async () => {
    if (!selectedIngredient || !movementForm.quantity) return;
    
    const quantity = movementForm.movement_type === 'purchase' || movementForm.movement_type === 'adjustment'
      ? Math.abs(parseFloat(movementForm.quantity))
      : -Math.abs(parseFloat(movementForm.quantity));
    
    try {
      await axios.post(`${API}/stock-movements`, {
        ingredient_id: selectedIngredient.ingredient_id,
        movement_type: movementForm.movement_type,
        quantity: quantity,
        unit_cost: movementForm.unit_cost ? parseFloat(movementForm.unit_cost) : null,
        notes: movementForm.notes
      }, { withCredentials: true });
      
      toast.success('Movimiento registrado');
      setShowMovementDialog(false);
      setMovementForm({ movement_type: 'purchase', quantity: '', unit_cost: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar movimiento');
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      category: 'general',
      unit: 'unidad',
      current_stock: 0,
      min_stock: 0,
      cost_per_unit: 0,
      supplier: ''
    });
  };

  const openEditDialog = (ingredient) => {
    setSelectedIngredient(ingredient);
    setForm({
      name: ingredient.name,
      category: ingredient.category,
      unit: ingredient.unit,
      current_stock: ingredient.current_stock,
      min_stock: ingredient.min_stock,
      cost_per_unit: ingredient.cost_per_unit,
      supplier: ingredient.supplier || ''
    });
    setShowEditDialog(true);
  };

  const openMovementDialog = (ingredient) => {
    setSelectedIngredient(ingredient);
    setShowMovementDialog(true);
  };

  const filteredIngredients = ingredients.filter(ing => {
    const matchesSearch = ing.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || ing.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-bold text-xl">Inventario</h1>
              <p className="text-sm text-muted-foreground">Control de stock</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {alerts.length > 0 && (
              <Badge variant="destructive" className="flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {alerts.length} alertas
              </Badge>
            )}
            <Button onClick={() => { resetForm(); setShowCreateDialog(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Insumo
            </Button>
          </div>
        </div>
      </header>

      <main className="p-4 max-w-6xl mx-auto">
        <Tabs defaultValue="inventory">
          <TabsList className="mb-4">
            <TabsTrigger value="inventory" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Inventario
            </TabsTrigger>
            <TabsTrigger value="movements" className="flex items-center gap-2">
              <History className="w-4 h-4" />
              Movimientos
            </TabsTrigger>
            <TabsTrigger value="alerts" className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Alertas ({alerts.length})
            </TabsTrigger>
          </TabsList>

          {/* Inventory Tab */}
          <TabsContent value="inventory">
            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar insumo..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Categoría" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las categorías</SelectItem>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Ingredients Grid */}
            {filteredIngredients.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Package className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No hay insumos</h3>
                  <p className="text-muted-foreground mb-4">Agrega tu primer insumo para empezar</p>
                  <Button onClick={() => { resetForm(); setShowCreateDialog(true); }}>
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Insumo
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredIngredients.map((ing) => (
                  <Card key={ing.ingredient_id} className={ing.is_low_stock ? 'border-red-300' : ''}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-semibold">{ing.name}</h3>
                          <Badge variant="outline" className="text-xs">
                            {CATEGORIES.find(c => c.value === ing.category)?.label || ing.category}
                          </Badge>
                        </div>
                        {ing.is_low_stock && (
                          <Badge variant="destructive">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Stock bajo
                          </Badge>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-2 mb-3">
                        <div className="text-center p-2 bg-muted rounded-lg">
                          <p className="text-2xl font-bold">{ing.current_stock}</p>
                          <p className="text-xs text-muted-foreground">{ing.unit}</p>
                        </div>
                        <div className="text-center p-2 bg-muted rounded-lg">
                          <p className="text-lg font-mono">${ing.cost_per_unit}</p>
                          <p className="text-xs text-muted-foreground">por {ing.unit}</p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => openMovementDialog(ing)}
                        >
                          <ShoppingCart className="w-4 h-4 mr-1" />
                          Movimiento
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => openEditDialog(ing)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-red-500"
                          onClick={() => handleDeleteIngredient(ing.ingredient_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Movements Tab */}
          <TabsContent value="movements">
            <Card>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  {movements.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No hay movimientos registrados</p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {movements.map((mov) => (
                        <div key={mov.movement_id} className="p-4 flex justify-between items-center">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              mov.quantity > 0 
                                ? 'bg-green-100 text-green-600' 
                                : 'bg-red-100 text-red-600'
                            }`}>
                              {mov.quantity > 0 
                                ? <TrendingUp className="w-5 h-5" />
                                : <TrendingDown className="w-5 h-5" />
                              }
                            </div>
                            <div>
                              <p className="font-medium">{mov.ingredient_name}</p>
                              <p className="text-sm text-muted-foreground">
                                {mov.movement_type === 'purchase' ? 'Compra' :
                                 mov.movement_type === 'consumption' ? 'Consumo' :
                                 mov.movement_type === 'waste' ? 'Merma' : 'Ajuste'}
                                {mov.notes && ` - ${mov.notes}`}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(mov.created_at).toLocaleString()} • {mov.created_by_name}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`text-lg font-bold ${
                              mov.quantity > 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {mov.quantity > 0 ? '+' : ''}{mov.quantity}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {mov.stock_before} → {mov.stock_after}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts">
            {alerts.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <AlertCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">¡Todo en orden!</h3>
                  <p className="text-muted-foreground">No hay alertas de stock bajo</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {alerts.map((alert) => (
                  <Card key={alert.ingredient_id} className="border-red-200 bg-red-50 dark:bg-red-950">
                    <CardContent className="p-4 flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="w-8 h-8 text-red-500" />
                        <div>
                          <h3 className="font-semibold">{alert.name}</h3>
                          <p className="text-sm text-muted-foreground">
                            Stock actual: {alert.current_stock} {alert.unit} | 
                            Mínimo: {alert.min_stock} {alert.unit}
                          </p>
                        </div>
                      </div>
                      <Badge variant="destructive">
                        Faltan {alert.shortage} {alert.unit}
                      </Badge>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog || showEditDialog} onOpenChange={(open) => {
        if (!open) { setShowCreateDialog(false); setShowEditDialog(false); }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{showEditDialog ? 'Editar Insumo' : 'Nuevo Insumo'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ej: Carne molida"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Categoría</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Unidad de medida</Label>
                <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map(u => (
                      <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Stock actual</Label>
                <Input
                  type="number"
                  value={form.current_stock}
                  onChange={(e) => setForm({ ...form, current_stock: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Stock mínimo (alerta)</Label>
                <Input
                  type="number"
                  value={form.min_stock}
                  onChange={(e) => setForm({ ...form, min_stock: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Costo por unidad</Label>
                <Input
                  type="number"
                  value={form.cost_per_unit}
                  onChange={(e) => setForm({ ...form, cost_per_unit: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Proveedor (opcional)</Label>
                <Input
                  value={form.supplier}
                  onChange={(e) => setForm({ ...form, supplier: e.target.value })}
                  placeholder="Nombre del proveedor"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); setShowEditDialog(false); }}>
              Cancelar
            </Button>
            <Button onClick={showEditDialog ? handleUpdateIngredient : handleCreateIngredient}>
              {showEditDialog ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Movement Dialog */}
      <Dialog open={showMovementDialog} onOpenChange={setShowMovementDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Movimiento</DialogTitle>
            <DialogDescription>
              {selectedIngredient?.name} - Stock actual: {selectedIngredient?.current_stock} {selectedIngredient?.unit}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Tipo de movimiento</Label>
              <Select 
                value={movementForm.movement_type} 
                onValueChange={(v) => setMovementForm({ ...movementForm, movement_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="purchase">Compra (entrada)</SelectItem>
                  <SelectItem value="consumption">Consumo (salida)</SelectItem>
                  <SelectItem value="waste">Merma/Pérdida</SelectItem>
                  <SelectItem value="adjustment">Ajuste de inventario</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Cantidad ({selectedIngredient?.unit})</Label>
              <Input
                type="number"
                value={movementForm.quantity}
                onChange={(e) => setMovementForm({ ...movementForm, quantity: e.target.value })}
                placeholder="0"
              />
            </div>
            {movementForm.movement_type === 'purchase' && (
              <div className="space-y-2">
                <Label>Costo unitario (opcional)</Label>
                <Input
                  type="number"
                  value={movementForm.unit_cost}
                  onChange={(e) => setMovementForm({ ...movementForm, unit_cost: e.target.value })}
                  placeholder="Actualiza el costo por unidad"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Notas (opcional)</Label>
              <Input
                value={movementForm.notes}
                onChange={(e) => setMovementForm({ ...movementForm, notes: e.target.value })}
                placeholder="Ej: Compra a Proveedor X"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMovementDialog(false)}>Cancelar</Button>
            <Button onClick={handleAddMovement}>Registrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
